import { useState, FC } from 'react';
import {
  Button,
  IconButton,
  TextField,
  Stack,
  InputAdornment,
  Tooltip,
  Box,
} from '@mui/material';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

import { useObtainAuthenticationEmail } from '../hooks';
import { FriendlyCaptcha } from './FriendlyCaptcha';
import { useGetSpacing } from '@ui';
import { CloseIcon } from '@ui/icons';
import { useAuthStore } from '@features/auth';

const loginSchema = z.object({
  email: z
    .string()
    // .min(1, { message: 'This field has to be filled.' })
    .email('This is not a valid email.'),
});

// const FieldInfo = ({ field }: { field: AnyFieldApi }) => {
//   return (
//     <>
//       {field.state.meta.isTouched && field.state.meta.errors.length ? (
//         <FormHelperText>
//           {field.state.meta.errors.map((err) => err.message).join(',')}
//         </FormHelperText>
//       ) : null}
//       {field.state.meta.isValidating ? 'Validating...' : null}
//     </>
//   );
// };

type Props = {
  emailLoginSuccess: () => void;
};

export const EmailAuthForm: FC<Props> = ({ emailLoginSuccess }) => {
  const { venueSlug } = useAuthStore();
  const { t } = useTranslation();

  const { mutateAsync, isPending } = useObtainAuthenticationEmail();
  const [captcha, setCaptcha] = useState('');
  const getSpacing = useGetSpacing();

  const form = useForm({
    defaultValues: {
      email: '',
    },
    onSubmit: async ({ value }) => {
      try {
        await mutateAsync({
          email: value.email,
          captcha: captcha,
          venue: venueSlug,
        });
        emailLoginSuccess();
      } catch (error) {
        console.error('error', error);
      } // Do something with form data
    },
    validators: {
      onBlur: loginSchema,
    },
  });

  const handleComplete = (response: string) => {
    setCaptcha(response);
  };

  return (
    <Stack
      spacing={getSpacing('l')}
      flexDirection={'column'}
      width={'100%'}
      component={'form'}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <form.Field
        name="email"
        children={(field) => {
          return (
            <Tooltip
              title={
                field.state.meta.isTouched && field.state.meta.errors.length > 0
                  ? field.state.meta.errors
                      .filter(
                        (err): err is { message: string } =>
                          !!err && typeof err.message === 'string'
                      )
                      .map((err) => err.message)
                      .join(',')
                  : ''
              }
              arrow
              open={field.state.meta.errors.length > 0}
            >
              <TextField
                id="email"
                label={t('yourEmailAddressLabel')}
                required
                fullWidth
                type="email"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                placeholder={t('yourEmailAddressPlaceholder')}
                error={field.state.meta.errors.length > 0}
                // helperText={
                //   field.state.meta.isTouched &&
                //   field.state.meta.errors.length > 0
                //     ? field.state.meta.errors
                //         .map((err) => err.message)
                //         .join(',')
                //     : ''
                // }
                slotProps={{
                  input: {
                    endAdornment: (
                      <>
                        {field.state.value && (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="close"
                              onClick={() => {
                                form.resetField('email');
                              }}
                              color="primary"
                            >
                              <CloseIcon />
                            </IconButton>
                          </InputAdornment>
                        )}
                      </>
                    ),
                  },
                }}
              />
            </Tooltip>
          );
        }}
      />

      <FriendlyCaptcha onComplete={handleComplete} />

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <form.Subscribe
          selector={(state) => [state.isValid && !state.isPristine]}
          children={([canSubmit, isSubmitting]) => {
            return (
              <Button
                variant="contained"
                type="submit"
                disabled={!canSubmit || captcha === ''}
                loading={isPending}
              >
                {isSubmitting ? '...' : t('loginWithEmail')}
              </Button>
            );
          }}
        />
      </Box>
    </Stack>
  );
};
